// Leitura, Cursos e Mídia · Relatório e meta anual (texto 16).
// Regra do período (A6):
//  · Concluídos (Lido / Assistido / Concluído), Parei e Abandonei entram pela DATA EM QUE MUDARAM DE SITUAÇÃO:
//    "Terminei em" / "Assisti em" / "Parei em"; sem ela, a data que o app registrou sozinho ao mudar a situação (A7);
//    sem nenhuma das duas (itens antigos), a data de criação, e o relatório avisa quantos foram assim.
//  · Em andamento (Lendo / Fazendo): entram nos períodos em que tiveram registro (páginas lidas, aulas assistidas).
//    Filmes e séries não têm registro diário: "Assistindo" entra se começou até o fim do período.
//  · "Quero ler / Quero assistir / Quero fazer" é a lista de agora (não depende do período).
import type { Reg } from '../store/types';

export type TipoLeitura = 'livro' | 'midia' | 'curso' | 'artigo';   // texto 21 C8: artigos e sites
export const TIPOS: TipoLeitura[] = ['livro', 'midia', 'curso', 'artigo'];

/** Situação → campo de data que ela preenche sozinha (A7). */
export const DATAS_SITUACAO: Record<TipoLeitura, Record<string, string>> = {
  livro: { Lendo: 'inicio', Lido: 'fim', Parei: 'parada' },
  midia: { Assistindo: 'inicio', Assistido: 'assistido', Parei: 'parada' },
  curso: { Fazendo: 'inicio', 'Concluído': 'fim', Parei: 'parada' },
  artigo: { Lendo: 'inicio', Lido: 'fim', Parei: 'parada' },
};
const FINAL: Record<TipoLeitura, string> = { livro: 'Lido', midia: 'Assistido', curso: 'Concluído', artigo: 'Lido' };
const ANDAMENTO: Record<TipoLeitura, string> = { livro: 'Lendo', midia: 'Assistindo', curso: 'Fazendo', artigo: 'Lendo' };
const QUERO: Record<TipoLeitura, string> = { livro: 'Quero ler', midia: 'Quero assistir', curso: 'Quero fazer', artigo: 'Quero ler' };

const hoje = () => { const d = new Date(); const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const num = (v: unknown) => { const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.')); return isFinite(n) ? n : 0; };

/** Ao mudar a situação: grava a data da situação nova (se o campo estiver vazio) e guarda quando mudou (A7). */
export function datasDaSituacao(tipo: TipoLeitura, c: Record<string, any>, novo: string): Record<string, any> {
  if (!novo || c.status === novo) return {};
  const campo = DATAS_SITUACAO[tipo][novo];
  return { mudou: { ...(c.mudou ?? {}), [novo]: hoje() }, ...(campo && !c[campo] ? { [campo]: hoje() } : {}) };
}

/** Data em que o item chegou à situação atual (e se é só a data de criação, para avisar). */
export function dataDaSituacao(tipo: TipoLeitura, r: Reg): { data: string; criacao: boolean } {
  const s = String(r.c.status ?? '');
  const campo = DATAS_SITUACAO[tipo][s];
  const d = (campo && String(r.c[campo] ?? '')) || String(r.c.mudou?.[s] ?? '');
  return d ? { data: d.slice(0, 10), criacao: false } : { data: String(r.criado ?? '').slice(0, 10), criacao: true };
}
export const tipoDe = (r: Reg): TipoLeitura | null => (r.col === 'livro' || r.col === 'midia' || r.col === 'curso' || r.col === 'artigo' ? r.col : null);
const abandonado = (r: Reg) => r.c.status === 'Parei' && r.c.voltar === 'Não, abandonei';
const dentro = (d: string, ini: string, fim: string) => !!d && d >= ini && d <= fim;
const logNoPeriodo = (r: Reg, ini: string, fim: string) => Object.entries((r.c.log ?? {}) as Record<string, number>).filter(([d, n]) => dentro(d, ini, fim) && num(n) > 0);

export type Linha = { r: Reg; tipo: TipoLeitura; situacao: string; data: string; semData: boolean; grupo: 'final' | 'andamento' | 'parei' | 'abandonei' | 'quero' };

/** Itens que aparecem no período, cada um com o motivo (grupo) e a data. */
export function itensDoPeriodo(regs: Reg[], ini: string, fim: string): Linha[] {
  const out: Linha[] = [];
  for (const r of regs) {
    const tipo = tipoDe(r); if (!tipo) continue;
    const s = String(r.c.status ?? '');
    const { data, criacao } = dataDaSituacao(tipo, r);
    if (s === FINAL[tipo] && dentro(data, ini, fim)) out.push({ r, tipo, situacao: s, data, semData: criacao, grupo: 'final' });
    else if (s === 'Parei' && dentro(data, ini, fim)) out.push({ r, tipo, situacao: s, data, semData: criacao, grupo: abandonado(r) ? 'abandonei' : 'parei' });
    else if (s === ANDAMENTO[tipo]) {
      const teve = tipo === 'midia' ? (String(r.c.inicio || data) <= fim) : logNoPeriodo(r, ini, fim).length > 0 || (!Object.keys(r.c.log ?? {}).length && dentro(data, ini, fim));
      if (teve) out.push({ r, tipo, situacao: s, data, semData: criacao, grupo: 'andamento' });
    } else if (s === QUERO[tipo]) out.push({ r, tipo, situacao: s, data, semData: criacao, grupo: 'quero' });
  }
  return out;
}

/** Páginas (livros) ou aulas (cursos) por dia no período, pelo registro diário. */
export function porDia(regs: Reg[], col: 'livro' | 'curso', ini: string, fim: string): Record<string, number> {
  const dias: Record<string, number> = {};
  for (const r of regs) if (r.col === col) for (const [d, n] of logNoPeriodo(r, ini, fim)) dias[d] = (dias[d] ?? 0) + num(n);
  return dias;
}
/** Maior sequência de dias seguidos com leitura dentro do período. */
export function melhorSequencia(dias: Record<string, number>): number {
  const ds = Object.keys(dias).filter(d => dias[d] > 0).sort();
  let melhor = 0, atual = 0, ant = '';
  for (const d of ds) {
    const seguinte = ant && new Date(d + 'T12:00:00').getTime() - new Date(ant + 'T12:00:00').getTime() === 86400000;
    atual = seguinte ? atual + 1 : 1; melhor = Math.max(melhor, atual); ant = d;
  }
  return melhor;
}

/** Quantos concluídos no ano (para a meta anual, B). */
export function concluidosNoAno(regs: Reg[], tipo: TipoLeitura, ano: string): number {
  return regs.filter(r => r.col === tipo && r.c.status === FINAL[tipo] && dataDaSituacao(tipo, r).data.startsWith(ano)).length;
}
/** Ritmo que falta para fechar a meta: itens por mês até dezembro (contando o mês atual). */
export function ritmoMeta(meta: number, feitos: number, ano: string): { faltam: number; porMes: number } {
  const faltam = Math.max(0, meta - feitos);
  const agora = new Date();
  const meses = String(agora.getFullYear()) === ano ? 12 - agora.getMonth() : String(agora.getFullYear()) < ano ? 12 : 1;
  return { faltam, porMes: faltam ? Math.ceil(faltam / Math.max(1, meses)) : 0 };
}
